import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Loader2, Save, AlertTriangle, CheckCircle2, Star, BadgeCheck,
  Plus, Trash2, ExternalLink, Calendar, Crown, Copy, Check, X, Upload, ChevronDown,
} from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import api from '../lib/api';

// ---- Helpers ----
function diasAteExpirar(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

// ---- Modal PIX ----
function ModalPix({ modalState, onClose, onAprovado, onEscolherPlano }) {
  const [copiado, setCopiado] = useState(false);
  const [statusLocal, setStatusLocal] = useState('pending');

  // Polling a cada 5s quando o PIX está exibido
  useEffect(() => {
    if (modalState?.step !== 'pix' || !modalState?.paymentId) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/pagamentos/status/${modalState.paymentId}`);
        const s = r.data.status;
        if (s === 'approved') {
          clearInterval(id);
          setStatusLocal('approved');
          setTimeout(() => onAprovado(), 1500);
        } else if (s === 'rejected' || s === 'cancelled') {
          clearInterval(id);
          setStatusLocal('rejected');
        }
      } catch {}
    }, 5000);
    return () => clearInterval(id);
  }, [modalState?.step, modalState?.paymentId, onAprovado]);

  const copiar = () => {
    navigator.clipboard.writeText(modalState.qrCodigo || '');
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  if (!modalState) return null;
  const { step, plano, qrBase64, qrCodigo, valor } = modalState;
  const nomePlano = plano === 'premium' ? 'Premium' : 'Básico';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="modal-pix-overlay">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-0">
          <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {step === 'escolha' ? 'Escolha seu plano' : step === 'aprovado' ? 'Pagamento confirmado!' : 'Pagar via PIX'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="modal-fechar-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* STEP: escolha de plano */}
          {step === 'escolha' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Escolha o plano para sua assinatura anual:</p>
              <button
                data-testid="escolher-basic-btn"
                onClick={() => onEscolherPlano('basic')}
                className="w-full border-2 border-gray-200 hover:border-[#1D9E75] rounded-xl p-4 text-left transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-[#0F6E56]">Plano Básico</p>
                    <p className="text-xs text-gray-500 mt-0.5">Aparece na busca pública por 1 ano</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-lg">R$29,90</p>
                    <p className="text-xs text-gray-400">/ano</p>
                  </div>
                </div>
              </button>
              <button
                data-testid="escolher-premium-btn"
                onClick={() => onEscolherPlano('premium')}
                className="w-full border-2 border-[#1D9E75]/40 hover:border-[#1D9E75] rounded-xl p-4 text-left transition-colors bg-[#F0FBF7] group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-[#0F6E56]">Plano Premium</p>
                      <Crown size={13} className="text-[#1D9E75]" />
                    </div>
                    <p className="text-xs text-[#0F6E56]/70 mt-0.5">Topo da busca + destaque no perfil</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0F6E56] text-lg">R$69,00</p>
                    <p className="text-xs text-[#0F6E56]/60">/ano</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* STEP: gerando PIX */}
          {step === 'carregando' && (
            <div className="py-8 flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-[#1D9E75]" />
              <p className="text-sm text-gray-600">Gerando PIX do plano {nomePlano}…</p>
            </div>
          )}

          {/* STEP: PIX pronto */}
          {step === 'pix' && statusLocal !== 'approved' && statusLocal !== 'rejected' && (
            <>
              <div className="bg-[#F0FBF7] rounded-xl p-3 text-center">
                <p className="text-xs text-[#0F6E56]/80">Plano <strong>{nomePlano}</strong></p>
                <p className="text-2xl font-bold text-[#0F6E56]">R${valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-[#0F6E56]/60">pagamento único anual via PIX</p>
              </div>

              {qrBase64 ? (
                <div className="flex justify-center">
                  <img
                    src={`data:image/png;base64,${qrBase64}`}
                    alt="QR Code PIX"
                    data-testid="pix-qrcode-img"
                    className="w-44 h-44 rounded-lg border border-gray-100"
                  />
                </div>
              ) : (
                <div className="w-44 h-44 mx-auto rounded-lg bg-gray-100 flex items-center justify-center">
                  <p className="text-xs text-gray-400">QR Code indisponível</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Código PIX (copia e cola)</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={qrCodigo || ''}
                    data-testid="pix-codigo-input"
                    className="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2 text-xs bg-gray-50 text-gray-700 truncate focus:outline-none"
                  />
                  <button
                    onClick={copiar}
                    data-testid="pix-copiar-btn"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors whitespace-nowrap
                      ${copiado ? 'bg-[#1D9E75] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    {copiado ? <><Check size={12} /> Copiado!</> : <><Copy size={12} /> Copiar</>}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500 justify-center pt-1">
                <Loader2 size={12} className="animate-spin" />
                Verificando pagamento automaticamente…
              </div>
            </>
          )}

          {/* STEP: aprovado */}
          {(step === 'aprovado' || statusLocal === 'approved') && (
            <div className="py-6 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 bg-[#E1F5EE] rounded-full flex items-center justify-center">
                <CheckCircle2 size={28} className="text-[#1D9E75]" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Pagamento confirmado!</p>
                <p className="text-sm text-gray-500 mt-1">Seu plano <strong>{nomePlano}</strong> foi ativado com sucesso.</p>
              </div>
            </div>
          )}

          {/* STEP: rejeitado */}
          {statusLocal === 'rejected' && (
            <div className="py-6 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center">
                <AlertTriangle size={28} className="text-red-500" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Pagamento não aprovado</p>
                <p className="text-sm text-gray-500 mt-1">Tente novamente gerando um novo PIX.</p>
              </div>
              <button
                onClick={onClose}
                className="mt-2 px-5 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Copiar Link ----
function CopiarLinkBtn({ slug }) {
  const [copiado, setCopiado] = useState(false);
  const link = `${window.location.origin}/profissional/${slug}`;

  const copiar = () => {
    const copy = () => {
      try {
        const el = document.createElement('textarea');
        el.value = link;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      } catch {}
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).catch(copy);
    } else {
      copy();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  return (
    <button
      onClick={copiar}
      data-testid="copiar-link-btn"
      title={link}
      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors
        ${copiado ? 'bg-[#E1F5EE] text-[#0F6E56]' : 'text-gray-600 hover:text-[#1D9E75] hover:bg-gray-50'}`}
    >
      {copiado ? <><Check size={13} /> Link copiado!</> : <><Copy size={13} /> Copiar link</>}
    </button>
  );
}

// ---- Status Banner ----
function StatusAssinatura({ user, onAssinar }) {
  const dias = diasAteExpirar(user.assinaturaExpiraEm);
  const expirado = dias != null && dias <= 0;
  const trial = user.plano === 'trial';
  const premium = user.plano === 'premium';

  if (expirado) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} className="text-red-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-red-900 text-sm">Assinatura expirada</p>
          <p className="text-xs text-red-700">Seu perfil está oculto da busca pública. Renove para voltar a aparecer.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="renovar-basic-btn" onClick={() => onAssinar('basic')} className="bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl px-3 py-2 text-xs whitespace-nowrap">
            Basic R$29,90
          </button>
          <button data-testid="renovar-premium-btn" onClick={() => onAssinar('premium')} className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-3 py-2 text-xs whitespace-nowrap flex items-center gap-1">
            <Crown size={11} /> Premium R$69,00
          </button>
        </div>
      </div>
    );
  }

  if (trial) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Calendar size={18} className="text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-amber-900 text-sm">Período de teste — {dias} dias restantes</p>
          <p className="text-xs text-amber-700">Após o trial, assine para continuar visível na busca.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="assinar-basic-btn" onClick={() => onAssinar('basic')} className="bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl px-3 py-2 text-xs whitespace-nowrap">
            Basic R$29,90
          </button>
          <button data-testid="assinar-premium-btn" onClick={() => onAssinar('premium')} className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-3 py-2 text-xs whitespace-nowrap flex items-center gap-1">
            <Crown size={11} /> Premium R$69,00
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#E1F5EE] border border-[#1D9E75]/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
      <div className="w-10 h-10 bg-[#1D9E75]/15 rounded-xl flex items-center justify-center flex-shrink-0">
        {premium ? <Crown size={18} className="text-[#1D9E75]" /> : <CheckCircle2 size={18} className="text-[#1D9E75]" />}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-[#0F6E56] text-sm">
          Plano {premium ? 'Premium (topo da lista)' : 'Básico'} — ativo
        </p>
        <p className="text-xs text-[#0F6E56]/80">Vence em {dias} dias.</p>
      </div>
      {!premium && (
        <button data-testid="upgrade-premium-btn" onClick={() => onAssinar('premium')} className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2 text-sm whitespace-nowrap flex items-center gap-1.5">
          <Crown size={13} /> Upgrade Premium
        </button>
      )}
    </div>
  );
}

// ---- Página Principal ----
export default function ProfissionalDashboard() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [modalState, setModalState] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);
  const [dropdownCatAberto, setDropdownCatAberto] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        nome: user.nome || '',
        telefone: user.telefone || '',
        bio: user.bio || '',
        cep: user.cep || '',
        rua: user.rua || '',
        numero: user.numero || '',
        complemento: user.complemento || '',
        bairro: user.bairro || '',
        cidade: user.cidade || '',
        estado: user.estado || '',
        avatarUrl: user.avatarUrl || '',
        categorias: user.categorias || [],
        servicos: user.servicos || [],
      });
    }
    api.get('/categorias').then(r => setCategoriasDisponiveis(r.data.map(c => c.nome))).catch(() => {});
  }, [user]);

  // Hooks (devem ficar antes de qualquer return condicional)
  const abrirPagamento = useCallback(async (plano) => {
    setModalState({ step: 'carregando', plano });
    try {
      const res = await api.post('/pagamentos/criar-pix', { plano });
      setModalState({
        step: 'pix',
        plano,
        paymentId: res.data.payment_id,
        qrBase64: res.data.qr_base64,
        qrCodigo: res.data.qr_codigo,
        valor: res.data.valor,
      });
    } catch (err) {
      setModalState(null);
      setFeedback({ ok: false, msg: formatApiErrorDetail(err?.response?.data?.detail) || 'Erro ao gerar PIX. Tente novamente.' });
      setTimeout(() => setFeedback(null), 4000);
    }
  }, []);

  const onAprovado = useCallback(async () => {
    setModalState(prev => ({ ...prev, step: 'aprovado' }));
    try {
      const res = await api.get('/auth/profissional/me');
      updateUser(res.data);
    } catch {}
    setTimeout(() => setModalState(null), 3000);
  }, [updateUser]);

  if (!form) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, avatarUrl: res.data.url }));
    } catch {
      setFeedback({ ok: false, msg: 'Erro ao enviar foto. Tente novamente.' });
      setTimeout(() => setFeedback(null), 3500);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const buscarCep = async (cep) => {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          rua: data.logradouro || prev.rua,
          bairro: data.bairro || prev.bairro,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado,
        }));
      }
    } catch {}
    finally { setBuscandoCep(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleLogout = () => { logout(); navigate('/'); };

  const addServico = () => setForm({ ...form, servicos: [...form.servicos, { id: `s-${Date.now()}`, nome: '', descricao: '' }] });
  const updServ = (idx, field, value) => {
    const next = [...form.servicos];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, servicos: next });
  };
  const delServ = (idx) => setForm({ ...form, servicos: form.servicos.filter((_, i) => i !== idx) });

  const salvar = async () => {
    setSalvando(true);
    try {
      const res = await api.patch('/profissional/me', form);
      updateUser(res.data);
      setFeedback({ ok: true, msg: 'Perfil salvo com sucesso!' });
    } catch (err) {
      setFeedback({ ok: false, msg: formatApiErrorDetail(err?.response?.data?.detail) || 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSalvando(false);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modal PIX */}
      {modalState && (
        <ModalPix
          modalState={modalState}
          onClose={() => setModalState(null)}
          onAprovado={onAprovado}
          onEscolherPlano={abrirPagamento}
        />
      )}

      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="w-9 h-9 bg-[#1D9E75] rounded-xl flex items-center justify-center text-white font-bold">G</Link>
            <div>
              <h1 className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Work Sans', sans-serif" }}>Meu painel</h1>
              <p className="text-[11px] text-gray-500">Olá, {user.nome.split(' ')[0]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/profissional/${user.slug}`} target="_blank" className="hidden sm:flex items-center gap-1.5 text-xs text-gray-600 hover:text-[#1D9E75] px-3 py-1.5 rounded-lg hover:bg-gray-50">
              <ExternalLink size={13} /> Ver perfil público
            </Link>
            <CopiarLinkBtn slug={user.slug} />
            <button onClick={handleLogout} data-testid="prof-logout-btn" className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-gray-50">
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <StatusAssinatura user={user} onAssinar={abrirPagamento} />

        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Perfil público</h2>
            <div className="flex items-center gap-2 text-xs">
              {user.verificado && <span className="inline-flex items-center gap-1 text-[#1D9E75] font-semibold"><BadgeCheck size={13} /> Verificado</span>}
              {user.totalAvaliacoes > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                  <Star size={13} fill="currentColor" /> {user.mediaAvaliacoes} ({user.totalAvaliacoes})
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Nome</label>
              <input value={form.nome} onChange={set('nome')} data-testid="dash-nome-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Telefone / WhatsApp</label>
              <input value={form.telefone} onChange={set('telefone')} data-testid="dash-tel-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>

            {/* CEP + auto-fill */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">CEP</label>
              <div className="flex gap-2">
                <input
                  value={form.cep}
                  onChange={e => setForm({ ...form, cep: e.target.value })}
                  onBlur={e => buscarCep(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  data-testid="dash-cep-input"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
                />
                <button
                  type="button"
                  onClick={() => buscarCep(form.cep)}
                  disabled={buscandoCep}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#E1F5EE] hover:bg-[#1D9E75] hover:text-white text-[#0F6E56] rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {buscandoCep ? <Loader2 size={13} className="animate-spin" /> : 'Buscar CEP'}
                </button>
              </div>
            </div>

            {/* Rua + Número */}
            <div className="sm:col-span-2 grid grid-cols-[1fr_100px] gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Rua / Logradouro</label>
                <input value={form.rua} onChange={set('rua')} placeholder="Preenchido automaticamente" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Número</label>
                <input value={form.numero} onChange={set('numero')} data-testid="dash-numero-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            {/* Complemento + Bairro */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Complemento</label>
              <input value={form.complemento} onChange={set('complemento')} placeholder="Casa, Apto, Sala…" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Bairro</label>
              <input value={form.bairro} onChange={set('bairro')} placeholder="Preenchido automaticamente" data-testid="dash-bairro-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>

            {/* Cidade + UF */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Cidade</label>
              <input value={form.cidade} onChange={set('cidade')} placeholder="Preenchido automaticamente" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">UF</label>
              <input value={form.estado} maxLength={2} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase().slice(0, 2) })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Foto de perfil</label>
              <div className="flex items-center gap-4">
                {/* Preview */}
                <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {form.avatarUrl
                    ? <img src={form.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    : <span className="text-2xl font-bold text-gray-300">{(form.nome || 'P')[0].toUpperCase()}</span>
                  }
                </div>
                {/* Upload button */}
                <div className="flex-1">
                  <label className={`cursor-pointer flex items-center gap-2 w-full border border-dashed border-gray-300 hover:border-[#1D9E75] rounded-xl px-3 py-2.5 text-sm text-gray-500 hover:text-[#1D9E75] transition-colors ${uploadingAvatar ? 'opacity-50 pointer-events-none' : ''}`}>
                    {uploadingAvatar
                      ? <><Loader2 size={14} className="animate-spin" /> Enviando…</>
                      : <><Upload size={14} /> {form.avatarUrl ? 'Trocar foto' : 'Selecionar foto'}</>
                    }
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="avatar-upload-input"
                      onChange={handleAvatarUpload}
                    />
                  </label>
                  {form.avatarUrl && (
                    <button onClick={() => setForm({ ...form, avatarUrl: '' })} className="mt-1.5 text-xs text-red-500 hover:text-red-700">
                      Remover foto
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Bio / Apresentação</label>
              <textarea value={form.bio} onChange={set('bio')} rows={3} data-testid="dash-bio-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" placeholder="Conte um pouco sobre seu trabalho..." />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Categorias de atuação</h2>
            <span className="text-xs text-gray-400">{form.categorias.length} selecionada{form.categorias.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Tags selecionadas */}
          {form.categorias.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {form.categorias.map(c => (
                <span key={c} className="inline-flex items-center gap-1 text-xs bg-[#E1F5EE] text-[#0F6E56] px-2.5 py-1 rounded-full">
                  {c}
                  <button type="button" onClick={() => setForm(p => ({ ...p, categorias: p.categorias.filter(x => x !== c) }))} className="ml-0.5 hover:text-red-600">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Dropdown */}
          <div className="relative">
            <button
              type="button"
              data-testid="categorias-dropdown-btn"
              onClick={() => setDropdownCatAberto(p => !p)}
              className="w-full flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-600 hover:border-[#1D9E75] focus:outline-none focus:ring-2 focus:ring-[#1D9E75] bg-white"
            >
              <span>{form.categorias.length === 0 ? 'Selecionar categorias…' : 'Adicionar mais categorias'}</span>
              <ChevronDown size={14} className={`transition-transform ${dropdownCatAberto ? 'rotate-180' : ''}`} />
            </button>
            {dropdownCatAberto && (
              <div className="absolute z-10 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                {categoriasDisponiveis.map(c => {
                  const sel = form.categorias.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setForm(p => ({
                          ...p,
                          categorias: sel ? p.categorias.filter(x => x !== c) : [...p.categorias, c],
                        }));
                      }}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${sel ? 'text-[#1D9E75] font-medium' : 'text-gray-700'}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${sel ? 'bg-[#1D9E75] border-[#1D9E75]' : 'border-gray-300'}`}>
                        {sel && <Check size={10} className="text-white" />}
                      </span>
                      {c}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Serviços oferecidos</h2>
            <button onClick={addServico} data-testid="add-servico-btn" className="text-xs bg-[#E1F5EE] text-[#1D9E75] hover:bg-[#1D9E75] hover:text-white font-medium rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-colors">
              <Plus size={13} /> Adicionar
            </button>
          </div>

          {form.servicos.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Nenhum serviço cadastrado ainda.</p>
          ) : (
            <div className="space-y-2">
              {form.servicos.map((s, idx) => (
                <div key={s.id} data-testid={`servico-${idx}`} className="border border-gray-100 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-start">
                  <input value={s.nome} onChange={(e) => updServ(idx, 'nome', e.target.value)} placeholder="Nome do serviço" className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
                  <input value={s.descricao} onChange={(e) => updServ(idx, 'descricao', e.target.value)} placeholder="Descrição curta" className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
                  <button onClick={() => delServ(idx)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 sm:self-center"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {feedback && (
          <div className={`rounded-xl px-4 py-2.5 text-sm ${feedback.ok ? 'bg-[#E1F5EE] text-[#0F6E56]' : 'bg-red-50 text-red-700'}`}>
            {feedback.msg}
          </div>
        )}

        <div className="sticky bottom-4">
          <button onClick={salvar} disabled={salvando} data-testid="salvar-perfil-btn"
            className="w-full bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
            {salvando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salvar alterações
          </button>
        </div>
      </main>
    </div>
  );
}
